import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';

// True when the API lives on a different site than this page. In that case the
// session cookie is a third-party cookie and Chrome may block it.
const isApiCrossSite = () => {
  try {
    const apiOrigin = new URL(api.defaults.baseURL, window.location.origin).origin;
    return apiOrigin !== window.location.origin;
  } catch (e) {
    return false;
  }
};

/**
 * Shown when login succeeded on the server but the browser did not keep the
 * session cookie (e.g. Chrome blocking third-party cookies).
 */
const CookieBlockedDialog = ({ open, onClose }) => {
  const { t } = useLanguage();
  const host = window.location.hostname;
  const intro = isApiCrossSite() ? t('cookieBlockedIntroCrossSite') : t('cookieBlockedIntroSameSite');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="cookie-blocked-title"
    >
      <DialogTitle id="cookie-blocked-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CookieOutlinedIcon color="warning" />
        {t('cookieBlockedTitle')}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {intro}
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {t('cookieBlockedWhyTitle')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('cookieBlockedWhy')}
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {t('cookieBlockedHowTitle')}
        </Typography>
        <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
          <Typography component="li" variant="body2">
            {t('cookieBlockedStepChrome1')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('cookieBlockedStepChrome2').replace('{host}', host)}
          </Typography>
          <Typography component="li" variant="body2">
            {t('cookieBlockedStepEdge').replace('{host}', host)}
          </Typography>
          <Typography component="li" variant="body2">
            {t('cookieBlockedStepReload')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('cookieBlockedClose')}</Button>
        <Button variant="contained" onClick={() => window.location.reload()}>
          {t('cookieBlockedReload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookieBlockedDialog;
